import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  PersonLinkRequest,
  PersonLinkStatus,
  CreateLinkRequestData,
  ReviewLinkRequestData,
  LinkRequestFilters,
  LinkRequestStats
} from '../types/personLink';
import { NotificationPriority } from '../types/user';
import { PersonService } from './personService';
import { NotificationService } from './notificationService';
import { AuditLogService } from './auditLogService';

/**
 * Service for managing person link requests (user claiming their person record)
 */
export class PersonLinkService {
  private static readonly COLLECTION = 'personLinkRequests';

  /**
   * Helper method to notify all admins of a new link request
   *
   * @param {PersonLinkRequest} request - The link request
   * @returns {Promise<void>}
   */
  private static async notifyAdminsOfNewRequest(request: PersonLinkRequest): Promise<void> {
    try {
      // Get all admin users
      const adminsQuery = query(
        collection(db, 'users'),
        where('role', '==', 'admin')
      );
      const snapshot = await getDocs(adminsQuery);

      // Create notifications for each admin
      const notificationPromises = snapshot.docs.map(adminDoc =>
        NotificationService.createNotification({
          userId: adminDoc.id,
          type: 'person_link_requested',
          title: 'New Profile Link Request',
          message: `${request.userName} has requested to link their account to ${request.personName}'s profile.`,
          priority: NotificationPriority.NORMAL,
          actionUrl: '/admin/link-requests',
          actionLabel: 'Review Request',
        })
      );

      await Promise.all(notificationPromises);
    } catch (error: any) {
      console.error('Error notifying admins of link request:', error);
      // Don't throw - notifications are non-critical
    }
  }

  /**
   * Create a new person link request
   *
   * @param {CreateLinkRequestData} data - Request data
   * @returns {Promise<PersonLinkRequest>} Created request
   * @throws {Error} If validation fails or creation fails
   */
  static async createLinkRequest(
    data: CreateLinkRequestData
  ): Promise<PersonLinkRequest> {
    try {
      // Check if user already has a pending request
      const existing = await this.getUserPendingRequest(data.userId);
      if (existing) {
        throw new Error('You already have a pending link request. Please wait for admin approval.');
      }

      // Check if user already linked to a person
      const existingLink = await this.getUserLinkedPerson(data.userId);
      if (existingLink) {
        throw new Error('You are already linked to a person. Contact an admin to change this.');
      }

      // Create request
      const requestData = {
        ...data,
        status: 'pending' as PersonLinkStatus,
        requestedAt: Timestamp.now(),
        createdAt: Timestamp.now(),
        createdBy: data.userId,
      };

      const docRef = await addDoc(collection(db, this.COLLECTION), requestData);

      const request: PersonLinkRequest = {
        id: docRef.id,
        ...data,
        status: 'pending',
        requestedAt: new Date(),
        createdAt: new Date(),
        createdBy: data.userId,
      };

      // Notify admins of new request
      await this.notifyAdminsOfNewRequest(request);

      return request;
    } catch (error: any) {
      console.error('Error creating link request:', error);
      throw new Error(`Failed to create link request: ${error.message}`);
    }
  }

  /**
   * Get user's pending link request (if any)
   *
   * @param {string} userId - User ID
   * @returns {Promise<PersonLinkRequest | null>} Pending request or null
   */
  static async getUserPendingRequest(
    userId: string
  ): Promise<PersonLinkRequest | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('userId', '==', userId),
        where('status', '==', 'pending'),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const docData = snapshot.docs[0];
      return {
        id: docData.id,
        ...docData.data(),
        requestedAt: docData.data().requestedAt?.toDate(),
        reviewedAt: docData.data().reviewedAt?.toDate(),
        createdAt: docData.data().createdAt?.toDate(),
      } as PersonLinkRequest;
    } catch (error: any) {
      console.error('Error getting user pending request:', error);
      return null;
    }
  }

  /**
   * Get user's linked person ID (from users collection)
   *
   * @param {string} userId - User ID
   * @returns {Promise<string | null>} Person ID or null
   */
  static async getUserLinkedPerson(userId: string): Promise<string | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return null;
      }
      return userDoc.data().personId || null;
    } catch (error: any) {
      console.error('Error getting user linked person:', error);
      return null;
    }
  }

  /**
   * Get all link requests (admin only)
   *
   * @param {LinkRequestFilters} [filters] - Optional filters
   * @returns {Promise<PersonLinkRequest[]>} All requests
   * @throws {Error} If query fails
   */
  static async getAllRequests(
    filters?: LinkRequestFilters
  ): Promise<PersonLinkRequest[]> {
    try {
      let q = query(collection(db, this.COLLECTION));

      // Apply filters
      if (filters?.status) {
        q = query(q, where('status', '==', filters.status));
      }
      if (filters?.userId) {
        q = query(q, where('userId', '==', filters.userId));
      }
      if (filters?.personId) {
        q = query(q, where('personId', '==', filters.personId));
      }

      // Always order by requestedAt descending
      q = query(q, orderBy('requestedAt', 'desc'));

      const snapshot = await getDocs(q);

      return snapshot.docs.map(docData => ({
        id: docData.id,
        ...docData.data(),
        requestedAt: docData.data().requestedAt?.toDate(),
        reviewedAt: docData.data().reviewedAt?.toDate(),
        createdAt: docData.data().createdAt?.toDate(),
        updatedAt: docData.data().updatedAt?.toDate(),
      })) as PersonLinkRequest[];
    } catch (error: any) {
      console.error('Error getting all requests:', error);
      throw new Error(`Failed to get link requests: ${error.message}`);
    }
  }

  /**
   * Get pending requests (admin only)
   *
   * @returns {Promise<PersonLinkRequest[]>} Pending requests
   */
  static async getPendingRequests(): Promise<PersonLinkRequest[]> {
    return this.getAllRequests({ status: 'pending' });
  }

  /**
   * Approve a link request (admin only)
   *
   * @param {ReviewLinkRequestData} reviewData - Review data
   * @returns {Promise<void>}
   * @throws {Error} If approval fails
   */
  static async approveRequest(
    reviewData: ReviewLinkRequestData
  ): Promise<void> {
    try {
      const { requestId, reviewedBy, reviewedByName, reviewNotes } = reviewData;

      // Get request
      const requestDoc = await getDoc(doc(db, this.COLLECTION, requestId));
      if (!requestDoc.exists()) {
        throw new Error('Link request not found');
      }

      const request = {
        id: requestDoc.id,
        ...requestDoc.data(),
      } as PersonLinkRequest;

      if (request.status !== 'pending') {
        throw new Error('Request has already been reviewed');
      }

      // Update user's personId
      await updateDoc(doc(db, 'users', request.userId), {
        personId: request.personId,
        updatedAt: Timestamp.now(),
      });

      // Update request status
      await updateDoc(doc(db, this.COLLECTION, requestId), {
        status: 'approved',
        reviewedAt: Timestamp.now(),
        reviewedBy,
        reviewedByName,
        reviewNotes: reviewNotes || '',
        updatedAt: Timestamp.now(),
      });

      // Notify user
      await NotificationService.createNotification({
        userId: request.userId,
        type: 'person_link_approved',
        title: 'Profile Link Approved',
        message: `Your request to link to ${request.personName}'s profile has been approved!`,
        priority: NotificationPriority.HIGH,
        actionUrl: `/people/${request.personId}`,
        actionLabel: 'View Profile',
      });

      // Log to audit trail
      await AuditLogService.logAction({
        userId: reviewedBy,
        action: 'user_approved',
        targetUserId: request.userId,
        details: {
          requestId,
          userName: request.userName,
          personId: request.personId,
          personName: request.personName,
          reviewNotes,
          actionType: 'approve_person_link',
        },
      });
    } catch (error: any) {
      console.error('Error approving link request:', error);
      throw new Error(`Failed to approve link request: ${error.message}`);
    }
  }

  /**
   * Reject a link request (admin only)
   *
   * @param {ReviewLinkRequestData} reviewData - Review data
   * @returns {Promise<void>}
   * @throws {Error} If rejection fails
   */
  static async rejectRequest(
    reviewData: ReviewLinkRequestData
  ): Promise<void> {
    try {
      const { requestId, reviewedBy, reviewedByName, reviewNotes } = reviewData;

      // Get request
      const requestDoc = await getDoc(doc(db, this.COLLECTION, requestId));
      if (!requestDoc.exists()) {
        throw new Error('Link request not found');
      }

      const request = {
        id: requestDoc.id,
        ...requestDoc.data(),
      } as PersonLinkRequest;

      if (request.status !== 'pending') {
        throw new Error('Request has already been reviewed');
      }

      // Update request status
      await updateDoc(doc(db, this.COLLECTION, requestId), {
        status: 'rejected',
        reviewedAt: Timestamp.now(),
        reviewedBy,
        reviewedByName,
        reviewNotes: reviewNotes || '',
        updatedAt: Timestamp.now(),
      });

      // Notify user
      await NotificationService.createNotification({
        userId: request.userId,
        type: 'person_link_rejected',
        title: 'Profile Link Request Denied',
        message: reviewNotes
          ? `Your request to link to ${request.personName}'s profile was denied: ${reviewNotes}`
          : `Your request to link to ${request.personName}'s profile was denied.`,
        priority: NotificationPriority.NORMAL,
        actionUrl: '/settings/profile',
      });

      // Log to audit trail
      await AuditLogService.logAction({
        userId: reviewedBy,
        action: 'user_rejected',
        targetUserId: request.userId,
        details: {
          requestId,
          userName: request.userName,
          personId: request.personId,
          personName: request.personName,
          reviewNotes,
          actionType: 'reject_person_link',
        },
      });
    } catch (error: any) {
      console.error('Error rejecting link request:', error);
      throw new Error(`Failed to reject link request: ${error.message}`);
    }
  }

  /**
   * Unlink user from person (admin only)
   *
   * @param {string} userId - User ID to unlink
   * @param {string} unlinkedBy - Admin user ID
   * @param {string} unlinkedByName - Admin display name
   * @param {string} [reason] - Optional reason for unlinking
   * @returns {Promise<void>}
   * @throws {Error} If unlinking fails
   */
  static async unlinkUser(
    userId: string,
    unlinkedBy: string,
    unlinkedByName: string,
    reason?: string
  ): Promise<void> {
    try {
      // Get current user
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const currentPersonId = userDoc.data().personId;
      if (!currentPersonId) {
        throw new Error('User is not linked to any person');
      }

      // Get person name for notification
      const person = await PersonService.getPersonById(currentPersonId);
      if (!person) {
        throw new Error('Person not found');
      }

      // Unlink user
      await updateDoc(doc(db, 'users', userId), {
        personId: null,
        updatedAt: Timestamp.now(),
      });

      // Find and update the approved link request
      const requestsSnapshot = await getDocs(
        query(
          collection(db, this.COLLECTION),
          where('userId', '==', userId),
          where('personId', '==', currentPersonId),
          where('status', '==', 'approved')
        )
      );

      // Update all matching requests to 'unlinked' status
      const updatePromises = requestsSnapshot.docs.map(reqDoc =>
        updateDoc(doc(db, this.COLLECTION, reqDoc.id), {
          status: 'rejected', // Mark as rejected so it doesn't show as approved
          reviewedAt: Timestamp.now(),
          reviewedBy: unlinkedBy,
          reviewedByName: unlinkedByName,
          reviewNotes: reason || 'Unlinked by admin',
          updatedAt: Timestamp.now(),
        })
      );

      await Promise.all(updatePromises);

      // Notify user
      await NotificationService.createNotification({
        userId,
        type: 'person_link_removed',
        title: 'Profile Link Removed',
        message: reason
          ? `Your link to ${person.firstName} ${person.lastName}'s profile was removed: ${reason}`
          : `Your link to ${person.firstName} ${person.lastName}'s profile was removed.`,
        priority: NotificationPriority.HIGH,
      });

      // Log to audit trail
      await AuditLogService.logAction({
        userId: unlinkedBy,
        action: 'role_changed',
        targetUserId: userId,
        details: {
          personId: currentPersonId,
          personName: `${person.firstName} ${person.lastName}`,
          reason,
          actionType: 'unlink_person',
        },
      });
    } catch (error: any) {
      console.error('Error unlinking user:', error);
      throw new Error(`Failed to unlink user: ${error.message}`);
    }
  }

  /**
   * Get link request statistics
   *
   * @returns {Promise<LinkRequestStats>} Statistics
   * @throws {Error} If fetching stats fails
   */
  static async getStats(): Promise<LinkRequestStats> {
    try {
      const allRequests = await this.getAllRequests();

      const pending = allRequests.filter(r => r.status === 'pending').length;
      const approved = allRequests.filter(r => r.status === 'approved').length;
      const rejected = allRequests.filter(r => r.status === 'rejected').length;

      // Calculate average review time (for approved and rejected)
      const reviewed = allRequests.filter(r => r.reviewedAt);
      const totalReviewTime = reviewed.reduce((sum, r) => {
        if (r.reviewedAt && r.requestedAt) {
          const diff = r.reviewedAt.getTime() - r.requestedAt.getTime();
          return sum + diff;
        }
        return sum;
      }, 0);

      const averageReviewTime = reviewed.length > 0
        ? totalReviewTime / reviewed.length / (1000 * 60 * 60) // Convert to hours
        : 0;

      return {
        total: allRequests.length,
        pending,
        approved,
        rejected,
        averageReviewTime,
      };
    } catch (error: any) {
      console.error('Error getting link request stats:', error);
      throw new Error(`Failed to get link request stats: ${error.message}`);
    }
  }

  /**
   * Check if person is linked to any user
   *
   * @param {string} personId - Person ID
   * @returns {Promise<string | null>} User ID or null
   */
  static async getPersonLinkedUser(personId: string): Promise<string | null> {
    try {
      // Query users collection for personId field
      const usersQuery = query(
        collection(db, 'users'),
        where('personId', '==', personId),
        limit(1)
      );

      const snapshot = await getDocs(usersQuery);

      if (snapshot.empty) {
        return null;
      }

      return snapshot.docs[0].id;
    } catch (error: any) {
      console.error('Error checking person linked user:', error);
      return null;
    }
  }

  /**
   * Get request by ID
   *
   * @param {string} requestId - Request ID
   * @returns {Promise<PersonLinkRequest | null>} Request or null
   */
  static async getRequestById(requestId: string): Promise<PersonLinkRequest | null> {
    try {
      const requestDoc = await getDoc(doc(db, this.COLLECTION, requestId));

      if (!requestDoc.exists()) {
        return null;
      }

      return {
        id: requestDoc.id,
        ...requestDoc.data(),
        requestedAt: requestDoc.data().requestedAt?.toDate(),
        reviewedAt: requestDoc.data().reviewedAt?.toDate(),
        createdAt: requestDoc.data().createdAt?.toDate(),
        updatedAt: requestDoc.data().updatedAt?.toDate(),
      } as PersonLinkRequest;
    } catch (error: any) {
      console.error('Error getting request by ID:', error);
      return null;
    }
  }

  /**
   * Get user's link request history
   *
   * @param {string} userId - User ID
   * @returns {Promise<PersonLinkRequest[]>} All requests for this user
   */
  static async getUserRequests(userId: string): Promise<PersonLinkRequest[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('userId', '==', userId),
        orderBy('requestedAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(docData => ({
        id: docData.id,
        ...docData.data(),
        requestedAt: docData.data().requestedAt?.toDate(),
        reviewedAt: docData.data().reviewedAt?.toDate(),
        createdAt: docData.data().createdAt?.toDate(),
        updatedAt: docData.data().updatedAt?.toDate(),
      })) as PersonLinkRequest[];
    } catch (error: any) {
      console.error('Error getting user requests:', error);
      throw new Error(`Failed to get user requests: ${error.message}`);
    }
  }
}
